import { db } from '../firebase';
import { supabase } from '../supabase';
import {
  collection,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  getDocs,
  getDoc
} from 'firebase/firestore';

/**
 * Firebase-Supabase Bidirectional Sync Service
 * 
 * This service syncs data between Firebase Firestore and Supabase PostgreSQL
 * to ensure data redundancy and prevent data loss.
 */

class DatabaseSyncService {
  constructor() {
    this.syncEnabled = true;
    this.syncQueue = [];
    this.listeners = [];
  }

  getStatusRank(status) {
    const statusOrder = {
      pending: 1,
      accepted: 2,
      'reached-site': 3,
      'in-progress': 4,
      'work-paused': 4,
      'work-completed': 5,
      completed: 5,
      'payment-done': 6,
      rejected: 99,
      cancelled: 99,
    };

    return statusOrder[status] ?? 0;
  }

  isBackwardStatusTransition(currentStatus, incomingStatus) {
    if (!currentStatus || !incomingStatus || currentStatus === incomingStatus) return false;
    return this.getStatusRank(incomingStatus) < this.getStatusRank(currentStatus);
  }

  /**
   * Convert Firebase timestamp to ISO string for Supabase
   */
  convertTimestamp(timestamp) {
    if (!timestamp) return null;
    if (typeof timestamp === 'string') return timestamp;
    if (timestamp.toDate) return timestamp.toDate().toISOString();
    if (timestamp instanceof Date) return timestamp.toISOString();
    return new Date(timestamp).toISOString();
  }

  /**
   * Map Firebase field names to Supabase column names.
   * Firebase and Supabase schemas can differ — this bridges the gap.
   */
  normalizeForSupabase(collectionName, data) {
    const out = { ...data };

    if (collectionName === 'serviceProviders') {
      // AddWorkerPage stores 'service'; Supabase schema uses 'serviceCategory'
      if (out.service !== undefined && out.serviceCategory === undefined) {
        out.serviceCategory = out.service;
      }
      // Ensure required-ish fields have a safe fallback
      if (!out.name) out.name = 'Unknown';
      if (!out.email) out.email = '';
      if (!out.serviceCategory) out.serviceCategory = out.service || 'General';
    }

    if (collectionName === 'users') {
      if (!out.name) out.name = out.displayName || 'Unknown';
      if (!out.email) out.email = '';
    }

    return out;
  }

  serialiseValue(value) {
    if (value === null || value === undefined) return null;
    if (value?.toDate) return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  parseExperienceYears(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const match = String(value).match(/\d+/);
    return match ? Number.parseInt(match[0], 10) : null;
  }

  buildDirectTablePayload(collectionName, docId, rawData) {
    const payload = {
      firebase_id: docId,
      data: rawData,
    };

    if (collectionName === 'serviceProviders') {
      payload.name = rawData.name || 'Unknown';
      payload.email = rawData.email || '';
      payload.phone = rawData.phone || null;
      payload.servicecategory = rawData.serviceCategory || rawData.service || 'General';
      payload.experience = this.parseExperienceYears(rawData.experience);
      payload.rating = rawData.rating ?? 0;
      payload.completedjobs = rawData.completedJobs ?? 0;
      payload.isverified = rawData.isVerified ?? false;
      payload.profilepicture = rawData.profilePicture || null;
      payload.bio = rawData.bio || null;
      payload.hourlyrate = rawData.hourlyRate ?? null;
      payload.availability = rawData.availability ?? null;
      payload.address = rawData.address || null;
      payload.createdat = rawData.createdAt || null;
      return payload;
    }

    if (collectionName === 'bookings') {
      payload.customerid = rawData.customerId || rawData.clientId || null;
      payload.customername = rawData.customerName || null;
      payload.customeremail = rawData.customerEmail || null;
      payload.customerphone = rawData.customerPhone || null;
      payload.providerid = rawData.providerId || null;
      payload.providername = rawData.providerName || null;
      payload.servicecategory = rawData.serviceCategory || rawData.service || rawData.serviceName || 'General';
      payload.servicename = rawData.serviceName || null;
      payload.description = rawData.description || null;
      payload.address = rawData.address || null;
      payload.preferreddate = rawData.preferredDate || rawData.scheduledDate || null;
      payload.preferredtime = rawData.preferredTime || rawData.scheduledTime || null;
      payload.status = rawData.status || 'pending';
      payload.urgencymode = rawData.urgencyMode || null;
      payload.visitfee = rawData.visitFee ?? null;
      payload.hourlyrate = rawData.hourlyRate ?? null;
      payload.estimatedprice = rawData.estimatedPrice ?? rawData.price ?? null;
      payload.finalprice = rawData.finalPrice ?? null;
      payload.calculatedtotal = rawData.calculatedTotal ?? null;
      payload.totalhoursworked = rawData.totalHoursWorked ?? null;
      payload.worksessions = rawData.workSessions ?? null;
      payload.statushistory = rawData.statusHistory ?? null;
      payload.pricingbreakdown = rawData.pricingBreakdown ?? null;
      payload.customerlatitude = rawData.customerLatitude ?? null;
      payload.customerlongitude = rawData.customerLongitude ?? null;
      payload.createdat = rawData.createdAt || null;
      payload.updatedat = rawData.updatedAt || null;
      payload.completedat = rawData.completedAt || null;
      return payload;
    }

    if (collectionName === 'reviews') {
      payload.bookingid = rawData.bookingId || rawData.bookingID || rawData.bookingid || rawData.booking || rawData.reviewId || docId;
      payload.customerid = rawData.customerId || rawData.clientId || null;
      payload.providerid = rawData.providerId || null;
      payload.rating = rawData.rating ?? null;
      payload.comment = rawData.comment || null;
      payload.createdat = rawData.createdAt || null;
      return payload;
    }

    return payload;
  }

  prepareForUpsert(collectionName, docId, rawData) {
    const jsonSafe = {};
    for (const [key, val] of Object.entries(rawData)) {
      jsonSafe[key] = this.serialiseValue(val);
    }

    if (['serviceProviders', 'bookings', 'reviews'].includes(collectionName)) {
      return this.buildDirectTablePayload(collectionName, docId, jsonSafe);
    }

    return {
      ...jsonSafe,
      firebase_id: docId,
    };
  }

  /**
   * Sync a document from Firebase to Supabase
   */
  async syncToSupabase(collectionName, docId, data, operation = 'upsert') {
    if (!this.syncEnabled) return;

    try {
      const normalized = this.normalizeForSupabase(collectionName, data);
      const payload = this.prepareForUpsert(collectionName, docId, normalized);

      if (operation === 'delete') {
        const { error } = await supabase
          .from(collectionName)
          .delete()
          .eq('firebase_id', docId);

        if (error) throw error;
        console.log(`✅ Deleted from Supabase: ${collectionName}/${docId}`);
      } else {
        // Upsert (insert or update)
        const { error } = await supabase
          .from(collectionName)
          .upsert(payload, { onConflict: 'firebase_id' });

        if (error) {
          console.error(`❌ Supabase upsert error for ${collectionName}/${docId}:`, error.message);
          console.error('   Hint: Run the schema fix SQL in your Supabase SQL Editor (see SUPABASE_SCHEMA_FIX.md)');
          throw error;
        }
        console.log(`✅ Synced to Supabase: ${collectionName}/${docId}`);
      }
    } catch (error) {
      console.error(`❌ Supabase sync error for ${collectionName}/${docId}:`, error.message || error);
      // Queue for retry
      this.syncQueue.push({ collectionName, docId, data, operation });
    }
  }

  /**
   * Sync a document from Supabase to Firebase
   */
  async syncToFirebase(collectionName, supabaseRow, operation = 'upsert') {
    if (!this.syncEnabled) return;

    try {
      const { firebase_id, id, created_at, updated_at, ...data } = supabaseRow;

      if (operation === 'delete' && firebase_id) {
        const docRef = doc(db, collectionName, firebase_id);
        await deleteDoc(docRef);
        console.log(`✅ Deleted from Firebase: ${collectionName}/${firebase_id}`);
      } else if (firebase_id) {
        if (collectionName === 'bookings' && data?.status) {
          const currentSnap = await getDoc(doc(db, collectionName, firebase_id));
          const currentData = currentSnap.exists() ? currentSnap.data() : null;
          const currentStatus = currentData?.status;

          if (this.isBackwardStatusTransition(currentStatus, data.status)) {
            console.warn(`⚠️ Skipping stale booking status sync for ${firebase_id}: ${data.status} < ${currentStatus}`);
            return;
          }
        }

        // Update existing document
        const docRef = doc(db, collectionName, firebase_id);
        await updateDoc(docRef, data);
        console.log(`✅ Synced to Firebase: ${collectionName}/${firebase_id}`);
      } else {
        // For service providers, use a stable doc ID (uid) whenever possible.
        // Random IDs create orphaned docs that the app cannot find by auth uid.
        const preferredId = collectionName === 'serviceProviders' ? data?.uid : null;

        if (preferredId) {
          await setDoc(doc(db, collectionName, preferredId), data, { merge: true });
          await supabase
            .from(collectionName)
            .update({ firebase_id: preferredId })
            .eq('id', id);
          console.log(`✅ Created in Firebase: ${collectionName}/${preferredId}`);
        } else {
          const docRef = await addDoc(collection(db, collectionName), data);

          // Update Supabase with the new Firebase ID
          await supabase
            .from(collectionName)
            .update({ firebase_id: docRef.id })
            .eq('id', id);

          console.log(`✅ Created in Firebase: ${collectionName}/${docRef.id}`);
        }
      }
    } catch (error) {
      console.error(`❌ Firebase sync error for ${collectionName}:`, error);
    }
  }

  /**
   * Setup Firebase to Supabase real-time sync for a collection
   */
  setupFirebaseListener(collectionName) {
    const collectionRef = collection(db, collectionName);

    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const docId = change.doc.id;
        const data = change.doc.data();

        if (change.type === 'added' || change.type === 'modified') {
          this.syncToSupabase(collectionName, docId, data, 'upsert');
        } else if (change.type === 'removed') {
          this.syncToSupabase(collectionName, docId, null, 'delete');
        }
      });
    }, (error) => {
      console.error(`❌ Firebase listener error for ${collectionName}:`, error);
    });

    this.listeners.push(unsubscribe);
    console.log(`🔄 Firebase listener setup for: ${collectionName}`);
  }

  /**
   * Setup Supabase to Firebase real-time sync for a table
   */
  setupSupabaseListener(tableName) {
    const channel = supabase
      .channel(`${tableName}_changes`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: tableName },
        (payload) => {
          console.log(`🔄 Supabase change detected in ${tableName}:`, payload);

          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            this.syncToFirebase(tableName, payload.new, 'upsert');
          } else if (payload.eventType === 'DELETE') {
            this.syncToFirebase(tableName, payload.old, 'delete');
          }
        }
      )
      .subscribe();

    console.log(`🔄 Supabase listener setup for: ${tableName}`);
    return channel;
  }

  /**
   * Initialize bidirectional sync for all collections
   */
  initializeBidirectionalSync() {
    const collections = ['users', 'serviceProviders', 'bookings', 'reviews'];

    console.log('🚀 Initializing bidirectional sync...');

    // Setup Firebase to Supabase listeners
    collections.forEach(collectionName => {
      this.setupFirebaseListener(collectionName);
    });

    // Setup Supabase to Firebase listeners
    collections.forEach(tableName => {
      this.setupSupabaseListener(tableName);
    });

    console.log('✅ Bidirectional sync initialized for all collections');
  }

  /**
   * Perform initial data migration from Firebase to Supabase
   */
  async migrateFirebaseToSupabase() {
    const collections = ['users', 'serviceProviders', 'bookings', 'reviews'];

    console.log('📦 Starting initial migration from Firebase to Supabase...');

    for (const collectionName of collections) {
      try {
        const snapshot = await getDocs(collection(db, collectionName));
        let count = 0;

        for (const docSnap of snapshot.docs) {
          await this.syncToSupabase(collectionName, docSnap.id, docSnap.data(), 'upsert');
          count++;
        }

        console.log(`✅ Migrated ${count} documents from ${collectionName}`);
      } catch (error) {
        console.error(`❌ Migration error for ${collectionName}:`, error);
      }
    }

    console.log('🎉 Migration completed!');
  }

  /**
   * Perform initial data migration from Supabase to Firebase
   */
  async migrateSupabaseToFirebase() {
    const tables = ['users', 'serviceProviders', 'bookings', 'reviews'];

    console.log('📦 Starting initial migration from Supabase to Firebase...');

    for (const tableName of tables) {
      try {
        const { data, error } = await supabase
          .from(tableName)
          .select('*');

        if (error) throw error;

        for (const row of data || []) {
          await this.syncToFirebase(tableName, row, 'upsert');
        }

        console.log(`✅ Migrated ${data?.length || 0} rows from ${tableName}`);
      } catch (error) {
        console.error(`❌ Migration error for ${tableName}:`, error);
      }
    }

    console.log('🎉 Migration completed!');
  }

  /**
   * Retry failed sync operations
   */
  async retryFailedSyncs() {
    const failedSyncs = [...this.syncQueue];
    this.syncQueue = [];

    for (const sync of failedSyncs) {
      await this.syncToSupabase(
        sync.collectionName,
        sync.docId,
        sync.data,
        sync.operation
      );
    }
  }

  /**
   * Stop all listeners
   */
  stopAllListeners() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
    console.log('🛑 All sync listeners stopped');
  }

  /**
   * Enable/disable sync
   */
  setSyncEnabled(enabled) {
    this.syncEnabled = enabled;
    console.log(`🔄 Sync ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create singleton instance
const syncService = new DatabaseSyncService();

export default syncService;
